import * as React from "react";
import { Formik } from "formik";
import { styles } from "./styles";
import Box from "@mui/material/Box";
import Loader from "components/loader";
import Button from "@mui/material/Button";
import CustomModal from "components/customModal";
import { useDispatch, useSelector } from "react-redux";
import { Close } from "@mui/icons-material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import {
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import AnimateButton from "components/animateButton";
import { TestimonialFormData, TestimonialData, ReducersModal } from "utils/modals";
import { TestimonialSchema } from "utils/schema";
import { GET_READ_SIGNED_URLS, UPLOAD_FILE_TO_S3, extractS3Key } from "utils/services";
import {
  ACCEPTED_IMAGE_ACCEPT,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_ACCEPT,
  ACCEPTED_VIDEO_TYPES,
  CATEGORY_DATA,
  FOLDER,
  MAX_IMAGE_SIZE_MB,
  MAX_VIDEO_SIZE_MB,
} from "utils/constants";
import { toServiceCategory } from "utils/serviceCategory";
import {
  displayFileLabel,
  formatFileSize,
  hasAwsSignedQuery,
  isLocalPreviewUrl,
  isStoredMediaUrl,
} from "utils/commonFunctions";
import { buildTestimonialPayload } from "utils/apiMappers";
import { createTestimonial, updateTestimonial } from "./action";

const CATEGORY_OPTIONS = CATEGORY_DATA.filter((item) => item.id !== "-1");

const storedThumbnailUrl = (value?: string) =>
  isStoredMediaUrl(value) ? `${value || ""}`.trim() : "";

const resolveSignedPreview = async (fileUrl: string) => {
  if (!fileUrl || isLocalPreviewUrl(fileUrl)) return fileUrl;
  if (hasAwsSignedQuery(fileUrl)) return fileUrl;
  try {
    const signedUrls = await GET_READ_SIGNED_URLS([fileUrl]);
    return signedUrls?.[0] || fileUrl;
  } catch {
    return fileUrl;
  }
};

const replacedS3Url = (previousUrl?: string, nextUrl?: string) => {
  const previous = storedThumbnailUrl(previousUrl);
  const next = storedThumbnailUrl(nextUrl);
  if (!previous) return "";
  if (!next) return previous;
  return extractS3Key(previous) !== extractS3Key(next) ? previous : "";
};

function MediaSlot({
  kind,
  src,
  alt,
  onClick,
}: {
  kind: "image" | "video";
  src: string;
  alt?: string;
  onClick?: () => void;
}) {
  return (
    <Box sx={styles.mediaFrame}>
      {kind === "image" ? (
        <img
          src={src}
          alt={alt || "Thumbnail preview"}
          onClick={onClick}
          style={{
            ...styles.mediaFit,
            cursor: onClick ? "pointer" : "default",
          }}
        />
      ) : (
        <video
          src={src}
          controls
          playsInline
          preload="metadata"
          onClick={onClick}
          style={styles.mediaFit}
        />
      )}
    </Box>
  );
}

function LocalMediaPreview({
  file,
  kind,
  alt,
  onClick,
}: {
  file: File;
  kind: "image" | "video";
  alt?: string;
  onClick?: () => void;
}) {
  const [url, setUrl] = React.useState("");

  React.useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file, kind]);

  if (!url) return <Box sx={styles.mediaFrame} />;

  return <MediaSlot kind={kind} src={url} alt={alt} onClick={onClick} />;
}

type Props = {
  state: boolean;
  setState: (open: boolean) => void;
  selectedTestimonial: TestimonialData | null;
};

export default function AddTestimonialModal({
  state,
  setState,
  selectedTestimonial,
}: Props) {
  const dispatch: any = useDispatch();
  const { category: selectedCategory } = useSelector(
    (state: ReducersModal) => state.TestimonialReducer,
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const thumbnailInputRef = React.useRef<HTMLInputElement>(null);
  const thumbnailFileRef = React.useRef<File | null>(null);
  const mediaFileRef = React.useRef<File | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isDraggingThumbnail, setIsDraggingThumbnail] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState("");
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = React.useState("");

  const isEdit = Boolean(selectedTestimonial);

  React.useEffect(() => {
    if (!state) return;
    let cancelled = false;
    const mediaStored = selectedTestimonial?.media || "";
    const thumbnailStored = selectedTestimonial?.thumbnail || "";
    const loadPreviews = async () => {
      const [mediaUrl, thumbnailUrl] = await Promise.all([
        resolveSignedPreview(
          selectedTestimonial?.mediaPreview || mediaStored,
        ),
        resolveSignedPreview(
          selectedTestimonial?.thumbnailPreview || thumbnailStored,
        ),
      ]);
      if (cancelled) return;
      setPreviewUrl(mediaUrl);
      setThumbnailPreviewUrl(thumbnailUrl);
    };
    loadPreviews();
    return () => {
      cancelled = true;
    };
  }, [state, selectedTestimonial]);

  const revokePreview = (url: string) => {
    if (isLocalPreviewUrl(url)) URL.revokeObjectURL(url);
  };

  const handleClose = (resetForm?: Function) => {
    revokePreview(previewUrl);
    revokePreview(thumbnailPreviewUrl);
    setPreviewUrl("");
    setThumbnailPreviewUrl("");
    setIsDragging(false);
    setIsDraggingThumbnail(false);
    setLoading(false);
    thumbnailFileRef.current = null;
    mediaFileRef.current = null;
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (thumbnailInputRef.current) thumbnailInputRef.current.value = "";
    resetForm?.();
    setState(false);
  };

  const initialValues = React.useMemo<TestimonialFormData>(
    () => ({
      ...new TestimonialFormData(),
      category: toServiceCategory(
        selectedTestimonial?.category ?? selectedCategory,
      ),
      title: selectedTestimonial?.title || "",
      existingThumbnailUrl: selectedTestimonial?.thumbnail || "",
      existingMediaUrl: selectedTestimonial?.media || "",
      thumbnailFile: null,
      mediaFile: null,
    }),
    [selectedTestimonial, selectedCategory],
  );

  const assignVideo = (
    file: File | undefined,
    setFieldValue: Function,
    setFieldTouched: Function,
  ) => {
    if (!file) return;
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      return;
    }
    revokePreview(previewUrl);
    setPreviewUrl(URL.createObjectURL(file));
    mediaFileRef.current = file;
    setFieldValue("mediaFile", file);
    setFieldTouched("mediaFile", true, false);
  };

  const assignThumbnail = (
    file: File | undefined,
    setFieldValue: Function,
    setFieldTouched: Function,
  ) => {
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return;
    }
    revokePreview(thumbnailPreviewUrl);
    const localPreview = URL.createObjectURL(file);
    setThumbnailPreviewUrl(localPreview);
    thumbnailFileRef.current = file;
    setFieldValue("thumbnailFile", file);
    setFieldTouched("thumbnailFile", false);
  };

  const handleOnSubmit = async (values: TestimonialFormData) => {
    const nextTitle = values.title.trim();
    const existingMedia = values.existingMediaUrl || "";
    const existingThumb = values.existingThumbnailUrl || "";
    const unchanged =
      isEdit &&
      nextTitle === (selectedTestimonial?.title || "").trim() &&
      toServiceCategory(values.category) ===
        toServiceCategory(selectedTestimonial?.category) &&
      !values.mediaFile &&
      !values.thumbnailFile &&
      existingMedia === (selectedTestimonial?.media || "") &&
      existingThumb ===
        (isStoredMediaUrl(selectedTestimonial?.thumbnail)
          ? selectedTestimonial?.thumbnail || ""
          : "");

    if (unchanged) {
      return;
    }

    setLoading(true);
    try {
      const thumbnailFile =
        values.thumbnailFile instanceof File
          ? values.thumbnailFile
          : thumbnailFileRef.current;
      const mediaFile =
        values.mediaFile instanceof File
          ? values.mediaFile
          : mediaFileRef.current;

      let mediaUrl = existingMedia;
      let thumbnailUrl = existingThumb;

      if (thumbnailFile instanceof File) {
        thumbnailUrl = await UPLOAD_FILE_TO_S3(thumbnailFile, FOLDER.SERVICE);
        if (!thumbnailUrl) {
          setLoading(false);
          return;
        }
      }

      if (mediaFile instanceof File) {
        mediaUrl = await UPLOAD_FILE_TO_S3(mediaFile, FOLDER.SERVICE);
        if (!mediaUrl) {
          setLoading(false);
          return;
        }
      }

      if (!thumbnailUrl) {
        setLoading(false);
        return;
      }
      if (!mediaUrl) {
        setLoading(false);
        return;
      }

      const payload = buildTestimonialPayload({
        category: values.category,
        thumbnail: thumbnailUrl,
        media: mediaUrl,
      });

      const replacedS3Urls = [
        replacedS3Url(selectedTestimonial?.thumbnail, payload.thumbnail),
        replacedS3Url(selectedTestimonial?.media, payload.media),
      ].filter(Boolean);

      const afterSave = (response: any) => {
        setLoading(false);
        if (response?.isSuccess) {
          handleClose();
        }
      };

      if (isEdit) {
        dispatch(
          updateTestimonial(
            selectedTestimonial?._id || selectedTestimonial?.id || "",
            payload,
            afterSave,
            replacedS3Urls,
          ),
        );
      } else {
        dispatch(createTestimonial(payload, afterSave));
      }
    } catch (err: any) {
      setLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={TestimonialSchema(isEdit)}
      onSubmit={handleOnSubmit}
    >
      {({
        errors,
        handleBlur,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        setFieldTouched,
        resetForm,
      }) => {
        const hasVideo = Boolean(
          values.mediaFile || values.existingMediaUrl,
        );
        const hasThumbnail = Boolean(
          values.thumbnailFile ||
            values.existingThumbnailUrl ||
            thumbnailPreviewUrl,
        );

        return (
          <>
            <CustomModal
              visible={state}
              title={isEdit ? "Edit Testimonial" : "Add Testimonial"}
              onClose={() => handleClose(resetForm)}
              onSubmit={handleSubmit}
              footer={
                <AnimateButton>
                  <Button
                    disableElevation
                    size="large"
                    type="submit"
                    variant="contained"
                    color="secondary"
                    disabled={loading}
                  >
                    {loading
                      ? isEdit
                        ? "Updating..."
                        : "Saving..."
                      : isEdit
                      ? "Update"
                      : "Save"}
                  </Button>
                </AnimateButton>
              }
            >
                  <FormControl
                    fullWidth
                    sx={styles.formGroup}
                    error={Boolean(touched.category && errors.category)}
                  >
                    <Typography sx={styles.fieldLabel}>
                      Category<span style={styles.requiredStar}>*</span>
                    </Typography>
                    <Select
                      name="category"
                      value={values.category}
                      onBlur={handleBlur}
                      onChange={(e) =>
                        setFieldValue(
                          "category",
                          toServiceCategory(e.target.value),
                        )
                      }
                      sx={styles.selectInput}
                    >
                      {CATEGORY_OPTIONS.map((item) => (
                        <MenuItem key={item.id} value={Number(item.id)}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.category && errors.category && (
                      <FormHelperText error>{errors.category}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    fullWidth
                    sx={styles.formGroup}
                    error={Boolean(
                      (touched.title && errors.title) ||
                        (touched.thumbnailFile && errors.thumbnailFile),
                    )}
                  >
                    <Typography sx={styles.fieldLabel}>
                      Thumbnail Image<span style={styles.requiredStar}>*</span>
                    </Typography>
                    {!hasThumbnail ? (
                      <Box
                        sx={{
                          ...styles.dropzone,
                          ...(isDraggingThumbnail
                            ? styles.dropzoneDragging
                            : {}),
                        }}
                        onClick={() => thumbnailInputRef.current?.click()}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDraggingThumbnail(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDraggingThumbnail(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDraggingThumbnail(false);
                          assignThumbnail(
                            e.dataTransfer.files?.[0],
                            setFieldValue,
                            setFieldTouched,
                          );
                        }}
                      >
                        <Box sx={styles.dropzoneIcon}>
                          <CloudUploadRoundedIcon />
                        </Box>
                        <Typography sx={styles.dropzoneTitle}>
                          Drag & drop a thumbnail here
                        </Typography>
                        <Typography sx={styles.dropzoneSub}>
                          or{" "}
                          <Box component="span" sx={styles.dropzoneBrowse}>
                            browse
                          </Box>{" "}
                          from your computer · JPG, PNG, WebP, AVIF up to{" "}
                          {MAX_IMAGE_SIZE_MB}MB
                        </Typography>
                        <input
                          ref={thumbnailInputRef}
                          type="file"
                          accept={ACCEPTED_IMAGE_ACCEPT}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            assignThumbnail(
                              e.target.files?.[0],
                              setFieldValue,
                              setFieldTouched,
                            );
                            e.target.value = "";
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={styles.previewCard}>
                        {values.thumbnailFile ? (
                          <LocalMediaPreview
                            file={values.thumbnailFile}
                            kind="image"
                            alt={values.title || "Thumbnail preview"}
                          />
                        ) : thumbnailPreviewUrl ? (
                          <MediaSlot
                            kind="image"
                            src={thumbnailPreviewUrl}
                            alt={values.title || "Thumbnail preview"}
                          />
                        ) : (
                          <Box sx={styles.mediaFrame} />
                        )}
                        <Box sx={styles.previewMeta}>
                          <Box sx={styles.previewInfo}>
                            <Box sx={styles.previewFileIcon}>
                              <ImageRoundedIcon fontSize="small" />
                            </Box>
                            <Box sx={styles.previewText}>
                              <Typography sx={styles.previewName}>
                                {values.thumbnailFile
                                  ? displayFileLabel(
                                      values.thumbnailFile.name,
                                      "Thumbnail image",
                                    )
                                  : "Existing thumbnail image"}
                              </Typography>
                              {values.thumbnailFile && (
                                <Typography sx={styles.previewSize}>
                                  {formatFileSize(values.thumbnailFile.size)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            sx={styles.previewRemove}
                            onClick={() => {
                              revokePreview(thumbnailPreviewUrl);
                              setThumbnailPreviewUrl("");
                              thumbnailFileRef.current = null;
                              setFieldValue("thumbnailFile", null);
                              setFieldValue("existingThumbnailUrl", "");
                              if (!isEdit) setFieldValue("title", "");
                              setFieldTouched("thumbnailFile", true);
                              if (thumbnailInputRef.current) {
                                thumbnailInputRef.current.value = "";
                              }
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                    {((touched.title && errors.title) ||
                      (touched.thumbnailFile && errors.thumbnailFile)) && (
                      <FormHelperText error>
                        {(errors.title || errors.thumbnailFile) as string}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl
                    fullWidth
                    sx={styles.formGroup}
                    error={Boolean(touched.mediaFile && errors.mediaFile)}
                  >
                    <Typography sx={styles.fieldLabel}>
                      Testimonial Video
                      <span style={styles.requiredStar}>*</span>
                    </Typography>
                    {!hasVideo ? (
                      <Box
                        sx={{
                          ...styles.dropzone,
                          ...(isDragging ? styles.dropzoneDragging : {}),
                        }}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setIsDragging(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setIsDragging(false);
                          assignVideo(
                            e.dataTransfer.files?.[0],
                            setFieldValue,
                            setFieldTouched,
                          );
                        }}
                      >
                        <Box sx={styles.dropzoneIcon}>
                          <CloudUploadRoundedIcon />
                        </Box>
                        <Typography sx={styles.dropzoneTitle}>
                          Drag & drop your video here
                        </Typography>
                        <Typography sx={styles.dropzoneSub}>
                          or{" "}
                          <Box component="span" sx={styles.dropzoneBrowse}>
                            browse
                          </Box>{" "}
                          from your computer · MP4, MPEG up to{" "}
                          {MAX_VIDEO_SIZE_MB}MB
                        </Typography>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept={ACCEPTED_VIDEO_ACCEPT}
                          style={{ display: "none" }}
                          onChange={(e) => {
                            assignVideo(
                              e.target.files?.[0],
                              setFieldValue,
                              setFieldTouched,
                            );
                            e.target.value = "";
                          }}
                        />
                      </Box>
                    ) : (
                      <Box sx={styles.previewCard}>
                        {values.mediaFile ? (
                          <LocalMediaPreview
                            file={values.mediaFile}
                            kind="video"
                          />
                        ) : previewUrl ? (
                          <MediaSlot kind="video" src={previewUrl} />
                        ) : (
                          <Box sx={styles.mediaFrame} />
                        )}
                        <Box sx={styles.previewMeta}>
                          <Box sx={styles.previewInfo}>
                            <Box sx={styles.previewFileIcon}>
                              <VideocamRoundedIcon fontSize="small" />
                            </Box>
                            <Box sx={styles.previewText}>
                              <Typography sx={styles.previewName}>
                                {values.mediaFile
                                  ? displayFileLabel(
                                      values.mediaFile.name,
                                      "Testimonial video",
                                    )
                                  : "Existing testimonial video"}
                              </Typography>
                              {values.mediaFile && (
                                <Typography sx={styles.previewSize}>
                                  {formatFileSize(values.mediaFile.size)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            sx={styles.previewRemove}
                            onClick={() => {
                              revokePreview(previewUrl);
                              setPreviewUrl("");
                              mediaFileRef.current = null;
                              setFieldValue("mediaFile", null);
                              setFieldValue("existingMediaUrl", "");
                              setFieldTouched("mediaFile", true);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    )}
                    {touched.mediaFile && errors.mediaFile && (
                      <FormHelperText error>
                        {errors.mediaFile as string}
                      </FormHelperText>
                    )}
                  </FormControl>
            </CustomModal>
            {loading && <Loader />}
          </>
        );
      }}
    </Formik>
  );
}
